"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { applyAppShellVerdict, readAppShellEnvironment, resolveAppShell } from "@/lib/app-shell";

/**
 * Resolves the app-shell verdict and publishes it — the half of the detection
 * that ALWAYS RUNS.
 *
 * WHY IT IS AN EFFECT AND NOT JUST THE INLINE SNIPPET. The pre-paint snippet in
 * the root layout cannot be relied on to execute: on a `notFound()` render
 * React materialises that subtree from the flight payload via DOM insertion
 * rather than HTML parsing, and a DOM-inserted <script> never executes (browser
 * rule, measured — see the banner in `lib/app-shell.ts`). Leaving detection to
 * the snippet alone meant the app rendered the BROWSER header on every 404,
 * which is the one screen where that hurts: no back control in the header, and
 * no browser back button either, so the user is stranded. An effect runs
 * wherever React runs, including that path.
 *
 * IT RENDERS NOTHING and it holds no state — the verdict lives on `<html>`
 * where CSS can read it, not in a context. There is no provider and no prop to
 * thread, for the same reason `useContextualHeader` derives instead of
 * declaring: a second source for something the document already states would
 * drift from it.
 *
 * IT RUNS ONCE PER DOCUMENT. Mounted in `ClientLayout`, which persists across
 * client-side navigations, so the attribute is set on the first commit and then
 * left alone. Nothing here re-runs on route changes, and nothing needs to: the
 * shell a document is running in cannot change while it is open.
 */
export default function AppShellProbe() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp, t0);
  return null;
}
function _temp() {
  const env = readAppShellEnvironment();
  if (!env) {
    return;
  }
  applyAppShellVerdict(resolveAppShell(env));
}